import { Updater, RowSelectionState } from '@tanstack/react-table';
import { LocalizedText, lt, showSuccessAlert, useDialogAction } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { DropDownButton, DropdownItem, IconName } from '@optimus-web/core';
import { PortfolioRateGroupOverview } from '@mews-commander/data-access';
import { copyToClipboard } from '@mews-commander/utils';
import { useDeleteLocalRateGroupsMutation } from '../../../queries';
import { DeleteLocalRateGroupDialog } from '../../sections/components/rateGroup/DeleteLocalRateGroupDialog';
import { RateGroupTabValues } from './types';
import { useLinkRateGroupsDialogForm } from './LinkRateGroupsWrapper';

interface Props {
    rateGroup: PortfolioRateGroupOverview;
    onCellClick: (args: { tab: RateGroupTabValues; rateGroup: PortfolioRateGroupOverview }) => void;
    setRowSelection: (updater: Updater<RowSelectionState>) => void;
}
export const RowActions = ({ rateGroup, onCellClick, setRowSelection }: Props) => {
    const { mutateAsync } = useDeleteLocalRateGroupsMutation();

    const deleteLocalRateGroups = async () => {
        await mutateAsync(
            { rateGroupIds: [rateGroup.id], enterpriseIds: [rateGroup.enterpriseId] },
            {
                onSuccess: () => {
                    showSuccessAlert({
                        title: (
                            <LocalizedText
                                textKey={TextKey.LocalRateGroupDeletedSuccessMessage}
                                substitutes={{ count: 1 }}
                            />
                        ),
                    });
                    setRowSelection(({ [rateGroup.id]: removed, ...otherSelection }) => otherSelection);
                },
            }
        );
    };

    const deleteAction = useDialogAction(deleteLocalRateGroups);
    const [renderLinkRateGroupsDialog, toggleLinkRateGroupsDialog] = useLinkRateGroupsDialogForm({
        selectedRateGroups: [rateGroup],
    });

    return (
        <>
            <DropDownButton icon={IconName.More} variant="ghost" size="small">
                <DropdownItem
                    label={<LocalizedText textKey={TextKey.ViewDetails} />}
                    onClick={() => onCellClick({ tab: 'details', rateGroup })}
                />
                <DropdownItem
                    label={<LocalizedText textKey={TextKey.LocalRateGroupsButtonLinkCentral} />}
                    disabled={rateGroup.isPortfolioManaged || !rateGroup.serviceKey}
                    onClick={toggleLinkRateGroupsDialog}
                />
                <DropdownItem
                    label={<LocalizedText textKey={TextKey.CopyRateGroupId} />}
                    onClick={() => {
                        if (copyToClipboard(rateGroup.id)) {
                            showSuccessAlert({ description: lt('CopiedSuccessMessage') });
                        }
                    }}
                />
                <DropdownItem
                    label={<LocalizedText textKey={TextKey.Delete} />}
                    variant="danger"
                    disabled={rateGroup.isPortfolioManaged || !!rateGroup.rates}
                    onClick={deleteAction.handlers.handleOpen}
                />
            </DropDownButton>
            {deleteAction.states.isDialogOpened && (
                <DeleteLocalRateGroupDialog
                    rateGroups={[rateGroup]}
                    isLoading={deleteAction.states.isProcessing}
                    onCancel={deleteAction.handlers.handleCancel}
                    onConfirm={deleteAction.handlers.handleConfirm}
                />
            )}
            {renderLinkRateGroupsDialog()}
        </>
    );
};
